import { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'sonner';
import { Camera, X, Truck, ArrowLeftRight, PackageCheck, Boxes, PackageOpen } from 'lucide-react';
import { getBearerToken } from '@/context/TrackOticAuthContext';

const GREEN = '#0B6B55';
const mono = { fontFamily: "'IBM Plex Mono', monospace" };
const API_BASE = process.env.REACT_APP_BACKEND_URL;
let guestToken = null; // in-memory only; the backend also sets the HttpOnly session cookie

async function scanApi(path, body) {
  const token = guestToken || getBearerToken();
  const res = await fetch(`${API_BASE}/api/scan${path}`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(typeof data.detail === 'string' ? data.detail : 'Refused');
    err.items = data.detail?.items || [];
    throw err;
  }
  return data;
}

const REASON_TEXT = {
  ALREADY_USED: 'Already used', EXPIRED: 'Expired', VOIDED: 'Voided label',
  NOT_OWNER_OR_PARTNER: 'Belongs to another customer', ALREADY_ACTIVE_ELSEWHERE: 'Already active on another trip',
  DEVICE_NOT_PROVISIONED: 'Device not ready', STAGE_REQUIRES_DEVICE: 'This stage needs a device',
  ALREADY_IN_CARTON: 'Already inside another carton — unpack it first',
  UNKNOWN_CODE: 'Code not recognised',
};

function CameraScanner({ onScan, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const scanner = new Html5Qrcode('qr-reader');
    ref.current = scanner;
    scanner.start({ facingMode: 'environment' }, { fps: 10, qrbox: 220 },
      (text) => { onScan(text); },
      () => {}).catch(e => toast.error(`Camera: ${e}`));
    return () => { scanner.stop().catch(() => {}); };
  }, [onScan]);
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex flex-col items-center justify-center p-4">
      <div id="qr-reader" className="w-full max-w-sm rounded-xl overflow-hidden bg-black" />
      <button data-testid="close-camera-btn" onClick={onClose}
        className="mt-4 rounded-full bg-white px-6 py-2 text-sm font-bold flex items-center gap-2">
        <X size={16} /> Close camera</button>
    </div>
  );
}

function ScanInput({ label, onCode, testid }) {
  const [manual, setManual] = useState('');
  const [camera, setCamera] = useState(false);
  const submit = () => { if (manual.trim()) { onCode(manual.trim()); setManual(''); } };
  const handleScan = useCallback((text) => { setCamera(false); onCode(text); }, [onCode]);
  return (
    <div className="mb-3">
      <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>{label}</label>
      <div className="flex gap-2">
        <input data-testid={`${testid}-input`} value={manual} onChange={e => setManual(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()}
          placeholder="Scan or type code…" className="flex-1 h-11 px-3 rounded-lg border bg-white text-sm"
          style={{ ...mono, borderColor: '#D9D6CD' }} />
        <button data-testid={`${testid}-add`} onClick={submit}
          className="h-11 px-4 rounded-lg text-white text-sm font-bold" style={{ background: GREEN }}>Add</button>
        <button data-testid={`${testid}-camera`} onClick={() => setCamera(true)}
          className="h-11 w-11 rounded-lg border bg-white flex items-center justify-center" style={{ borderColor: '#D9D6CD' }}>
          <Camera size={18} style={{ color: GREEN }} /></button>
      </div>
      {camera && <CameraScanner onScan={handleScan} onClose={() => setCamera(false)} />}
    </div>
  );
}

function Feedback({ item }) {
  if (!item) return null;
  const style = item.feedback === 'OK' ? { background: '#E7F2EE', color: GREEN }
    : item.feedback === 'WARNING' ? { background: '#FBF3E6', color: '#8C6A2B' }
      : { background: '#FBEBEB', color: '#8C2B2B' };
  return <div className="rounded-lg px-3 py-2 text-sm font-medium mb-3" style={style} data-testid="scan-feedback">{item.message}</div>;
}

export default function ScanPage() {
  const [action, setAction] = useState('DISPATCH');
  const [device, setDevice] = useState(null);
  const [passports, setPassports] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [invoice, setInvoice] = useState('');
  const [receiver, setReceiver] = useState('');
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);
  const [noDevice, setNoDevice] = useState(false);
  const [guest, setGuest] = useState(null);
  const [carton, setCarton] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invite = params.get('invite');
    if (!invite) return;
    fetch(`${API_BASE}/api/scan/guest-login`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: invite }),
    }).then(async r => {
      const data = await r.json();
      if (!r.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Invite invalid');
      guestToken = data.access_token;
      setGuest(data);
      window.history.replaceState({}, '', '/scan');
      toast.success(`Signed in as ${data.guest_name} — scan-only session for ${data.expires_in_hours}h`);
    }).catch(e => toast.error(e.message));
  }, []);

  const reset = () => { setDevice(null); setPassports([]); setFeedback(null); setSuccess(null); setInvoice(''); setReceiver(''); setNoDevice(false); setCarton(null); };

  const isPackMode = action === 'PACK' || action === 'UNPACK';

  const onCartonCode = async (raw) => {
    try {
      const res = await scanApi('/resolve', { raw });
      setFeedback(res);
      if (res.kind === 'PASSPORT' && ['DORMANT', 'ACTIVE'].includes(res.passport_state)) {
        setCarton({ raw, code: res.passport_short_code });
      } else if (res.kind !== 'PASSPORT') {
        toast.error('Carton must be a Track0tic passport sticker');
      }
    } catch (e) { toast.error(e.message); }
  };

  const onDeviceCode = async (raw) => {
    try {
      const res = await scanApi('/resolve', { raw });
      setFeedback(res);
      if (res.kind === 'DEVICE' && res.feedback === 'OK') setDevice({ raw, id: res.track0tic_id });
    } catch (e) { toast.error(e.message); }
  };

  const onPassportCode = async (raw) => {
    if (passports.some(p => p.raw === raw)) { toast.warning('Already in the list'); return; }
    try {
      const res = await scanApi('/resolve', { raw });
      if (isPackMode) {
        if (res.kind === 'DEVICE') { toast.error('That is a device QR, not a pack'); return; }
        const external = res.kind !== 'PASSPORT';
        setPassports([...passports, { raw, code: external ? raw : res.passport_short_code, external }]);
        return;
      }
      setFeedback(res);
      if (res.kind !== 'PASSPORT') return;
      const okState = action === 'DISPATCH' ? res.passport_state === 'DORMANT' : res.passport_state === 'ACTIVE';
      if (!okState) return;
      setPassports([...passports, { raw, code: res.passport_short_code, product_name: '', batch_no: '' }]);
    } catch (e) { toast.error(e.message); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      if (action === 'DISPATCH') {
        const res = await scanApi('/dispatch', {
          device_raw: device.raw,
          passports: passports.map(p => ({ raw: p.raw, product_name: p.product_name || null, batch_no: p.batch_no || null })),
        });
        setSuccess({ title: `Trip ${res.code} started`, sub: `${res.passports_count} passports Dormant → Active on ${res.track0tic_id}` });
      } else if (action === 'HANDOFF') {
        const res = await scanApi('/handoff', {
          device_raw: noDevice ? null : device?.raw, passports: passports.map(p => p.raw),
        });
        setSuccess({ title: `${res.legs_opened} passport(s) moved`, sub: res.warnings.join(' · ') || 'New leg opened' });
        res.warnings.forEach(w => toast.warning(w));
      } else if (action === 'PACK') {
        const res = await scanApi('/pack', { carton_raw: carton.raw, passports: undefined, children: passports.map(p => p.raw) });
        setSuccess({ title: `${res.linked} pack(s) linked`, sub: `Carton ${res.carton} now holds ${res.packs_total} pack(s)` });
      } else if (action === 'UNPACK') {
        const res = await scanApi('/unpack', { carton_raw: carton.raw, children: passports.length ? passports.map(p => p.raw) : null });
        setSuccess({ title: `${res.unlinked} pack(s) removed`, sub: `Carton ${res.carton} · ${res.packs_remaining} pack(s) remaining` });
      } else {
        const res = await scanApi('/deliver', {
          passports: passports.map(p => p.raw), invoice_no: invoice, receiver_name: receiver,
        });
        setSuccess({ title: 'Delivered', sub: `${res.certificates.length} certificate(s) issued for invoice ${invoice}` });
      }
    } catch (e) {
      (e.items || []).forEach(i => toast.error(`${i.raw}: ${REASON_TEXT[i.reason] || i.reason}`));
      if (!e.items?.length) toast.error(e.message);
    }
    setBusy(false);
  };

  const needsDevice = action === 'DISPATCH' || (action === 'HANDOFF' && !noDevice);
  const canConfirm = isPackMode
    ? (carton && (action === 'UNPACK' || passports.length > 0))
    : passports.length > 0 && (!needsDevice || device)
      && (action !== 'DELIVER' || (invoice.trim() && receiver.trim()));

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#F4F3EF' }} data-testid="scan-success">
        <div className="max-w-sm w-full text-center">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: '#E7F2EE' }}>
            <PackageCheck size={30} style={{ color: GREEN }} /></div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#1B1D1C' }}>{success.title}</h1>
          <p className="text-sm mb-6" style={{ color: '#6b7069' }}>{success.sub}</p>
          <button data-testid="scan-again-btn" onClick={reset}
            className="w-full rounded-xl py-3 text-white font-bold" style={{ background: GREEN }}>Scan again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-5" style={{ background: '#F4F3EF', fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="scan-page">
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold mb-1" style={{ color: '#1B1D1C' }}>Track0tic · Scan</h1>
        {guest && <p className="text-xs mb-3 px-2 py-1 rounded inline-block" style={{ background: '#E7F2EE', color: GREEN }} data-testid="guest-session-badge">
          Guest session · {guest.partner}</p>}
        <div className="grid grid-cols-5 gap-1.5 mb-5 mt-3">
          {[['DISPATCH', 'Dispatch', Truck], ['HANDOFF', 'Handoff', ArrowLeftRight], ['DELIVER', 'Deliver', PackageCheck], ['PACK', 'Pack', Boxes], ['UNPACK', 'Unpack', PackageOpen]].map(([v, l, Icon]) => (
            <button key={v} data-testid={`action-${v}`} onClick={() => { setAction(v); reset(); }}
              className="rounded-xl border py-3 flex flex-col items-center gap-1 text-sm font-bold transition-colors"
              style={action === v ? { background: '#E7F2EE', borderColor: GREEN, color: GREEN } : { background: 'white', borderColor: '#D9D6CD', color: '#4b504a' }}>
              <Icon size={18} />{l}</button>
          ))}
        </div>

        <Feedback item={feedback} />

        {isPackMode && (
          <div className="rounded-xl bg-white border p-4 mb-4" style={{ borderColor: '#E5E3DC' }}>
            <p className="text-xs font-bold tracking-wide mb-2" style={{ color: GREEN }}>STEP 1 · SCAN CARTON PASSPORT</p>
            {carton
              ? <div className="flex items-center justify-between">
                <span className="font-bold" style={mono}>{carton.code}</span>
                <button data-testid="clear-carton-btn" onClick={() => setCarton(null)} className="text-xs underline" style={{ color: '#8C2B2B' }}>Change</button>
              </div>
              : <ScanInput label="Carton passport sticker" onCode={onCartonCode} testid="carton" />}
          </div>
        )}

        {!isPackMode && action !== 'DELIVER' && (
          <div className="rounded-xl bg-white border p-4 mb-4" style={{ borderColor: '#E5E3DC' }}>
            <p className="text-xs font-bold tracking-wide mb-2" style={{ color: GREEN }}>
              STEP 1 · {action === 'DISPATCH' ? 'SCAN DEVICE QR' : 'SCAN NEW DEVICE QR'}</p>
            {device
              ? <div className="flex items-center justify-between">
                <span className="font-bold" style={mono}>{device.id}</span>
                <button data-testid="clear-device-btn" onClick={() => setDevice(null)} className="text-xs underline" style={{ color: '#8C2B2B' }}>Change</button>
              </div>
              : !noDevice && <ScanInput label="Device Track0tic ID" onCode={onDeviceCode} testid="device" />}
            {action === 'HANDOFF' && (
              <label className="flex items-center gap-2 text-sm mt-2" style={{ color: '#6b7069' }}>
                <input type="checkbox" data-testid="no-device-checkbox" checked={noDevice}
                  onChange={e => { setNoDevice(e.target.checked); setDevice(null); }} />
                No device at this stage (if the stage allows)</label>
            )}
          </div>
        )}

        <div className="rounded-xl bg-white border p-4 mb-4" style={{ borderColor: '#E5E3DC' }}>
          <p className="text-xs font-bold tracking-wide mb-2" style={{ color: GREEN }}>
            {isPackMode ? `STEP 2 · SCAN PACKS${action === 'UNPACK' ? ' (empty = remove all)' : ''}` : action === 'DELIVER' ? 'STEP 1 · SCAN PASSPORTS' : 'STEP 2 · SCAN PASSPORTS'}</p>
          <ScanInput label={isPackMode ? 'Pack code (Track0tic passport or pre-printed code)' : 'Passport sticker'} onCode={onPassportCode} testid="passport" />
          {passports.map((p, i) => (
            <div key={p.raw} className="flex items-center justify-between rounded-lg border px-3 py-2 mb-2"
              style={{ borderColor: '#EDEBE4', background: '#FAF9F6' }} data-testid={`passport-item-${p.code}`}>
              <span className="font-bold text-sm" style={mono}>{p.code}{p.external && <span className="ml-2 text-xs font-normal px-1.5 py-0.5 rounded" style={{ background: '#EFEDE6', color: '#6b7069' }}>external</span>}</span>
              <button data-testid={`remove-passport-${p.code}`}
                onClick={() => setPassports(passports.filter((_, j) => j !== i))}>
                <X size={16} style={{ color: '#8C2B2B' }} /></button>
            </div>
          ))}
          {passports.length === 0 && <p className="text-xs" style={{ color: '#9a9e96' }}>No passports scanned yet</p>}
        </div>

        {action === 'DELIVER' && (
          <div className="rounded-xl bg-white border p-4 mb-4" style={{ borderColor: '#E5E3DC' }}>
            <p className="text-xs font-bold tracking-wide mb-2" style={{ color: GREEN }}>STEP 2 · INVOICE AND RECEIVER</p>
            <input data-testid="invoice-input" value={invoice} onChange={e => setInvoice(e.target.value)} placeholder="Invoice number"
              className="w-full h-11 px-3 rounded-lg border bg-white text-sm mb-2" style={{ borderColor: '#D9D6CD' }} />
            <input data-testid="receiver-input" value={receiver} onChange={e => setReceiver(e.target.value)} placeholder="Receiver name"
              className="w-full h-11 px-3 rounded-lg border bg-white text-sm" style={{ borderColor: '#D9D6CD' }} />
          </div>
        )}

        <button data-testid="confirm-scan-btn" disabled={!canConfirm || busy} onClick={confirm}
          className="w-full rounded-xl py-4 text-white font-bold text-lg disabled:opacity-40" style={{ background: GREEN }}>
          {action === 'DISPATCH' ? `Confirm — activate ${passports.length || ''} passport(s)`
            : action === 'HANDOFF' ? 'Confirm handoff'
              : action === 'PACK' ? `Confirm — link ${passports.length || ''} pack(s)`
                : action === 'UNPACK' ? (passports.length ? `Confirm — remove ${passports.length} pack(s)` : 'Confirm — remove all packs')
                  : 'Confirm delivery'}</button>
        <p className="text-xs mt-3 text-center" style={{ color: '#9a9e96' }}>
          One confirm applies everything together. Nothing changes until you confirm.</p>
      </div>
    </div>
  );
}
